import { FC, useEffect, useState } from 'react';
import { BASE_URL } from '@/shared/api';
import { getAuthToken } from '@/utils/auth';

type Head = {
  messageId: string;
  enqueuedAt: string | null;
  deliveryCount: number;
  ageSeconds: number | null;
  preview: string;
  reason?: string;
};

type Side = {
  entity: string;
  active: number | null;
  deadLetter: number | null;
  scheduled: number | null;
  transfer: number | null;
  head: Head | null;
  deadHead: Head | null;
  error: string | null;
};

type QueueBus = {
  applies: boolean;
  source: Side | null;
  destination: Side | null;
  replicas: {
    namespace: string | null;
    source: number | null;
    mapping: number | null;
    destination: number | null;
    error: string | null;
  };
  hold: {
    parked: boolean;
    status: string;
    resumeAt: string | null;
    scaleBackAt: string | null;
    backlogAtPark: number | null;
    moved: number;
    returned: number;
    messagesPerSecond: number | null;
    parkEntity: string | null;
    error: string | null;
    sourcePaused: boolean;
  } | null;
};

type QueueBusPanelProps = {
  integrationId: string;
  rateLimited: boolean;
};

function looksLikeBus(config: any): boolean {
  return [config?.source, config?.destination].some((side) => {
    if (!side) return false;
    const azure = side.azureConfig || side.settings?.azureConfig;
    if (azure?.entityPath || azure?.queueName || azure?.topicName || azure?.namespace) return true;
    const text = [side.type, side.connectorType, side.systemId, side.libraryId, side.provider, side.name, side.entityPath]
      .filter((part) => typeof part === 'string')
      .join(' ')
      .toLowerCase();
    return /service bus|event hub|kafka|pubsub|rabbit|amqp|\bnxc\b|queue|bus/.test(text);
  });
}

export function shouldShowQueueBus(config: any, runs: Array<{ sourceQueueSize?: unknown; destinationQueueSize?: unknown }>): boolean {
  if (looksLikeBus(config)) return true;
  return runs.some((run) => run.sourceQueueSize != null || run.destinationQueueSize != null);
}

function toLocalInput(iso: string): string {
  const date = new Date(iso);
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function behind(seconds: number | null): string {
  if (seconds == null) return 'Age unknown';
  if (seconds < 90) return 'At realtime';
  const minutes = Math.round(seconds / 60);
  if (minutes < 120) return `${minutes} min behind realtime`;
  return `${Math.round(minutes / 60)} h behind realtime`;
}

async function postAction(integrationId: string, action: string, body: Record<string, unknown>) {
  const token = getAuthToken();
  if (!token) throw new Error('Sign in again to change the queue.');
  const response = await fetch(`${BASE_URL}/api-connectors/${integrationId}/queue-bus/${action}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(payload?.message || 'The queue action did not complete.');
  return payload;
}

const QueueBusPanel: FC<QueueBusPanelProps> = ({ integrationId, rateLimited }) => {
  const [bus, setBus] = useState<QueueBus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [replicas, setReplicas] = useState(3);
  const [scaleBack, setScaleBack] = useState('');
  const [acceptReorder, setAcceptReorder] = useState(false);
  const [resumeAt, setResumeAt] = useState('');
  const [pace, setPace] = useState(5);
  const [confirm, setConfirm] = useState<'drain' | 'source' | 'park' | 'dead' | null>(null);

  const load = async () => {
    const token = getAuthToken();
    if (!token) return;
    const response = await fetch(`${BASE_URL}/api-connectors/${integrationId}/queue-bus`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload?.message || 'The queue could not be read.');
    setBus(payload);
    setError(null);
  };

  useEffect(() => {
    let cancelled = false;
    load().catch((err) => {
      if (!cancelled) setError(err instanceof Error ? err.message : 'The queue could not be read.');
    });
    return () => {
      cancelled = true;
    };
  }, [integrationId]);

  const run = async (label: string, action: string, body: Record<string, unknown>, keepConfirm = false) => {
    setBusy(label);
    setError(null);
    try {
      await postAction(integrationId, action, body);
      if (!keepConfirm) setConfirm(null);
      await load();
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The queue action did not complete.');
      return false;
    } finally {
      setBusy(null);
    }
  };

  const source = bus?.source;
  const head = source?.head;
  const poison = (head?.deliveryCount || 0) >= 3 || (source?.deadLetter || 0) > 0;
  const hold = bus?.hold;
  const holdStatus = hold?.status;
  const moving = holdStatus === 'draining' || holdStatus === 'rehydrating';
  const sourceReplicas = bus?.replicas?.source;

  useEffect(() => {
    if (!moving) return undefined;
    const timer = setInterval(() => {
      load().catch((err) => setError(err instanceof Error ? err.message : 'The queue could not be read.'));
    }, 4000);
    return () => clearInterval(timer);
  }, [moving, integrationId]);

  return (
    <section className="studio-panel">
      <div className="studio-panel__head">
        <h3>Queue and bus</h3>
        <button type="button" className="studio-btn studio-btn--ghost" onClick={() => load().catch((err) => setError(err.message))} disabled={Boolean(busy)}>
          Refresh depth
        </button>
      </div>
      {error ? <p className="studio-error">{error}</p> : null}
      {!bus ? <p className="studio-note">Reading the bus…</p> : null}
      {bus && !bus.applies ? (
        <p>Recent runs recorded a queue depth, and this source is not a bus the studio can peek. Scaling still changes the worker pods.</p>
      ) : null}
      {source ? (
        <>
          <div className="studio-stats">
            <div className={`studio-stat ${(source.active || 0) > 0 ? 'is-warn' : 'is-ok'}`}><span>Waiting</span><strong>{source.active ?? '—'}</strong></div>
            <div className={`studio-stat ${(source.deadLetter || 0) > 0 ? 'is-bad' : 'is-neutral'}`}><span>Dead letter</span><strong>{source.deadLetter ?? '—'}</strong></div>
            <div className="studio-stat"><span>Scheduled</span><strong>{source.scheduled ?? '—'}</strong></div>
            <div className="studio-stat"><span>Transfer</span><strong>{source.transfer ?? '—'}</strong></div>
            <div className={`studio-stat ${(head?.ageSeconds || 0) > 300 ? 'is-warn' : 'is-ok'}`}><span>Head</span><strong>{behind(head?.ageSeconds ?? null)}</strong></div>
          </div>
          <p className="studio-note">
            {source.entity ? `${source.entity}. ` : ''}
            Waiting is the backlog the next consumer will take. Transfer is messages mid-forward to another entity. Scheduled messages are not due yet. A message locked by a live pod is not in these counts; the head below is the next available message. Dead letter is where poison messages sit after retries.
            {source.error ? ` ${source.error}` : ''}
          </p>
          {head ? (
            <article className={`studio-finding ${poison ? 'is-bad' : 'is-ok'}`}>
              <strong>
                Head {head.messageId || 'without an id'}
                {head.deliveryCount ? ` · delivered ${head.deliveryCount} times` : ''}
              </strong>
              <p>{head.preview || 'The head has no readable body.'}</p>
            </article>
          ) : null}
          {source.deadHead ? (
            <article className="studio-finding is-warn">
              <strong>Dead-letter head {source.deadHead.messageId || ''}{source.deadHead.reason ? ` · ${source.deadHead.reason}` : ''}</strong>
              <p>{source.deadHead.preview || 'No readable body.'}</p>
            </article>
          ) : null}
        </>
      ) : null}
      {bus?.destination?.active != null ? (
        <p className="studio-note">Destination bus is holding {bus.destination.active} messages{bus.destination.entity ? ` on ${bus.destination.entity}` : ''}.</p>
      ) : null}

      <div className="studio-rewind">
        <label>
          Workers
          <input type="number" min={1} max={8} value={replicas} onChange={(event) => setReplicas(Number(event.target.value))} />
        </label>
        <label>
          Scale back to 1
          <input type="datetime-local" value={scaleBack} onChange={(event) => setScaleBack(event.target.value)} />
        </label>
        <button type="button" className="studio-btn" disabled={Boolean(busy)} onClick={() => setConfirm('drain')}>
          Scale mapping and destination
        </button>
        <label className="studio-check">
          <input type="checkbox" checked={acceptReorder} onChange={(event) => setAcceptReorder(event.target.checked)} />
          Source may reorder
        </label>
        <button type="button" className="studio-btn studio-btn--ghost" disabled={Boolean(busy)} onClick={() => setConfirm('source')}>
          Scale source
        </button>
      </div>
      <p className="studio-note">
        Mapping and destination are the pods that clear a backlog. They return to 1 at the time you set, and that time has to be inside 24 hours.
        More than one source consumer can take messages out of order. Source is {sourceReplicas ?? 'unknown'} now, mapping {bus?.replicas?.mapping ?? 'unknown'}, destination {bus?.replicas?.destination ?? 'unknown'}.
        {bus?.replicas?.error ? ` ${bus.replicas.error}` : ''}
        {bus?.hold?.scaleBackAt ? ` Scale-back is set for ${new Date(bus.hold.scaleBackAt).toLocaleString()}.` : ''}
      </p>
      {rateLimited ? <p className="studio-error">Recent runs were rate limited. More pods will hit that limit harder.</p> : null}
      {confirm === 'drain' || confirm === 'source' ? (
        <div className="studio-confirm">
          <p>
            {confirm === 'drain'
              ? `Run ${replicas} mapping pods and ${replicas} destination pods until ${scaleBack ? new Date(scaleBack).toLocaleString() : 'a time you still need to set'}.`
              : `Run ${replicas} source pods. ${replicas > 1 ? 'Messages can arrive out of order.' : ''}`}
            {rateLimited ? ' This destination has been rate limiting.' : ''}
          </p>
          <div>
            <button
              type="button"
              disabled={busy === 'scale'}
              onClick={async () => {
                const scaleBackAt = scaleBack ? new Date(scaleBack).toISOString() : undefined;
                if (confirm === 'drain') {
                  const mappingOk = await run('scale', 'scale', { role: 'mapping', replicas, scaleBackAt }, true);
                  if (mappingOk) await run('scale', 'scale', { role: 'destination', replicas, scaleBackAt });
                  return;
                }
                run('scale', 'scale', { role: 'source', replicas, scaleBackAt, acceptReorder });
              }}
            >
              {busy === 'scale' ? 'Scaling…' : 'Scale'}
            </button>
            <button type="button" className="keep" onClick={() => setConfirm(null)}>Keep</button>
          </div>
        </div>
      ) : null}

      <div className="studio-rewind">
        <label>
          Feed the backlog back
          <input type="datetime-local" value={resumeAt} onChange={(event) => setResumeAt(event.target.value)} />
        </label>
        <label>
          Messages each second
          <input type="number" min={1} max={50} value={pace} onChange={(event) => setPace(Number(event.target.value))} />
        </label>
        {holdStatus === 'parked' || holdStatus === 'error' ? (
          <button type="button" className="studio-btn" disabled={Boolean(busy)} onClick={() => run('resume', 'resume', {})}>
            {busy === 'resume' ? 'Starting…' : holdStatus === 'error' ? 'Try again' : 'Replay now'}
          </button>
        ) : (
          <button type="button" className="studio-btn studio-btn--danger" disabled={Boolean(busy) || moving} onClick={() => setConfirm('park')}>
            {moving ? 'Moving the backlog…' : 'Set the backlog aside'}
          </button>
        )}
        <button type="button" className="studio-btn studio-btn--ghost" disabled={sourceReplicas !== 0 || !head || Boolean(busy) || moving} onClick={() => setConfirm('dead')}>
          Move the head aside
        </button>
      </div>
      <p className="studio-note">
        This moves the current backlog onto a new side topic and leaves the live topic for new messages, so the integration can get back to realtime. The source pauses only while that move runs, then it starts again. At the time you pick, within 7 days, those messages are published back onto the live topic or queue at the pace you set.
        {hold?.parkEntity ? ` Side topic ${hold.parkEntity}.` : ''}
        {holdStatus === 'draining' ? ` Moved ${hold?.moved ?? 0} so far.` : ''}
        {holdStatus === 'parked' ? ` ${hold?.moved ?? 0} messages are waiting${hold?.resumeAt ? ` until ${new Date(hold.resumeAt).toLocaleString()}` : ''}.` : ''}
        {holdStatus === 'rehydrating' ? ` Fed back ${hold?.returned ?? 0} of ${hold?.moved ?? 0} at ${hold?.messagesPerSecond || pace} per second.` : ''}
        {hold?.sourcePaused ? ' The source is paused until the move finishes.' : ''}
        {hold?.error ? ` ${hold.error}` : ''}
        {' '}Moving the head aside dead-letters that one message. Pause the source first, so a live consumer cannot take it at the same time. A poison message left in the backlog comes back with the replay.
      </p>
      {confirm === 'park' ? (
        <div className="studio-confirm">
          <p>
            Move messages already on the source onto a new side topic. New messages stay on the live topic. The source pauses during the move, then starts again.
            At {resumeAt ? new Date(resumeAt).toLocaleString() : 'a time you still need to set'}, feed the side topic back at {pace || 'a pace you still need to set'} messages a second.
            Replaying onto a topic delivers to every subscription on that topic.
          </p>
          <div>
            <button
              type="button"
              disabled={busy === 'park'}
              onClick={() => run('park', 'park', { resumeAt: resumeAt ? new Date(resumeAt).toISOString() : '', messagesPerSecond: pace })}
            >
              {busy === 'park' ? 'Starting…' : 'Set aside'}
            </button>
            <button type="button" className="keep" onClick={() => setConfirm(null)}>Keep</button>
          </div>
        </div>
      ) : null}
      {confirm === 'dead' ? (
        <div className="studio-confirm">
          <p>Move {head?.messageId || 'the head'} to the dead-letter queue. It can be inspected there and is no longer blocking the active queue.</p>
          <div>
            <button type="button" disabled={busy === 'dead'} onClick={() => run('dead', 'dead-letter', { messageId: head?.messageId || '' })}>
              {busy === 'dead' ? 'Moving…' : 'Move aside'}
            </button>
            <button type="button" className="keep" onClick={() => setConfirm(null)}>Keep</button>
          </div>
        </div>
      ) : null}
    </section>
  );
};

export default QueueBusPanel;
